// Which of the three looks the console wears.
//
// Per DEVICE, not per profile: a DM on a bright laptop at the table and a dark
// desktop at home wants a different answer in each place, and a synced theme
// would fight them. That is why this is localStorage and not a layout field.
//
// The attribute goes on <html> so the tokens are in scope for everything,
// including anything portalled outside #root.
export const THEMES = ["compass", "dark", "light"] as const;
export type Theme = (typeof THEMES)[number];
export const DEFAULT_THEME: Theme = "compass";
const KEY = "dmScreen.theme";

export const isTheme = (v: unknown): v is Theme => THEMES.includes(v as Theme);

/** The stored choice, or the default. Never throws: a blocked or cleared store
 *  (private window, wiped site data) must still render a usable console. */
export function readTheme(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    return isTheme(v) ? v : DEFAULT_THEME;
  } catch { return DEFAULT_THEME; }
}

export function applyTheme(theme: Theme): void {
  const el = typeof document === "undefined" ? null : document.documentElement;
  if (el) el.dataset.theme = theme;
  try { localStorage.setItem(KEY, theme); } catch { /* a theme is not worth failing over */ }
}

/** The next theme in the cycle, for a single-button switcher. */
export function nextTheme(theme: Theme): Theme {
  return THEMES[(THEMES.indexOf(theme) + 1) % THEMES.length];
}

export const THEME_LABEL: Record<Theme, string> = { compass: "Compass", dark: "Dark", light: "Light" };
export const THEME_GLYPH: Record<Theme, string> = { compass: "✦", dark: "☾", light: "☀" };

// HOW MUCH ROOM THE CONSOLE TAKES.
//
// Separate from the theme because they answer different questions: the theme is
// what it looks like, the density is how much of it fits. Comfortable is the
// default and what the screen was designed at; compact is for the DM who is
// mid-session with a map, a tracker and an activity rail all open at once, and
// wants the headings to stop spending their screen.
//
// Compact also hides the explanatory lead paragraph under each page title. That
// text is worth having the first few times and pure overhead afterwards, which
// is exactly what a density switch is for - it comes back the moment the DM
// switches back.
export const DENSITIES = ["comfortable", "compact"] as const;
export type Density = (typeof DENSITIES)[number];
export const DEFAULT_DENSITY: Density = "comfortable";
const DKEY = "dmScreen.density";

export const isDensity = (v: unknown): v is Density => DENSITIES.includes(v as Density);

export function readDensity(): Density {
  try {
    const v = localStorage.getItem(DKEY);
    return isDensity(v) ? v : DEFAULT_DENSITY;
  } catch { return DEFAULT_DENSITY; }
}

export function applyDensity(density: Density): void {
  const el = typeof document === "undefined" ? null : document.documentElement;
  if (el) el.dataset.density = density;
  try { localStorage.setItem(DKEY, density); } catch { /* a density is not worth failing over */ }
}

export const otherDensity = (d: Density): Density => (d === "compact" ? "comfortable" : "compact");
export const DENSITY_LABEL: Record<Density, string> = { comfortable: "Comfortable", compact: "Compact" };
export const DENSITY_GLYPH: Record<Density, string> = { comfortable: "▤", compact: "▦" };
